import { Checkbox, Form, Input, Row, Select, Space, TabsProps } from 'antd';
import React from 'react';
import { Banner, Card, Gap, Gutter, Tabs, Text } from '@common/components';
import {
  CreateWorkforceGroupInputData,
  WorkforceGroupOrgBusinessInputData,
} from '@scheduling/components';
import { clsx } from 'clsx';
import { Colors } from 'works/legacy/lib/style-helper';

export type WorkforceGroupFormProps = {
  extraTabs?: TabsProps['items'];
  shiftDistributionHeader?: () => React.ReactNode;
  onUpdateInputData: (
    key: string,
    value: string | boolean | number | [number, number]
  ) => void;
  inputData:
    | Partial<CreateWorkforceGroupInputData>
    | Partial<WorkforceGroupOrgBusinessInputData>
    | null;
  disableName?: boolean;
  disabled?: boolean;
  showBanner: boolean;
  bannerContent?: string | React.ReactElement;
};

export function WorkforceGroupForm({
  extraTabs = [],
  shiftDistributionHeader,
  onUpdateInputData,
  inputData,
  disableName = false,
  disabled = false,
  showBanner,
  bannerContent,
}: WorkforceGroupFormProps) {
  const renderBanner = () => {
    return <Banner showIcon type="info" content={bannerContent} />;
  };

  const tabItems: TabsProps['items'] = [
    ...extraTabs,
    {
      key: 'shiftDistribution',
      label: 'Shift Distribution',
      children: (
        <>
          <Gutter direction="vertical" size="md">
            <Gap size="md" vertical>
              {shiftDistributionHeader?.()}
              {showBanner && renderBanner()}
              <Card className="wk-wfg-form__shift-distribution-card">
                <Gap vertical size="sm">
                  <Row>
                    <Checkbox
                      onChange={(e) =>
                        onUpdateInputData(
                          'gateEarliestAdvanceTime',
                          e.target.checked
                        )
                      }
                      checked={inputData?.gateEarliestAdvanceTime}
                      disabled={disabled}
                      className={clsx({
                        'wk-wfg-form-checkbox-disabled': disabled,
                      })}
                    >
                      Limit shift visibility
                    </Checkbox>
                  </Row>
                  <Row>
                    <Gutter direction="left" size="lg">
                      <Gutter direction="bottom">
                        <Text color={disabled ? 'midnightLightest' : undefined}>
                          How far in advance should staff see shifts in the
                          mobile app?
                        </Text>
                      </Gutter>
                      <Space>
                        <Input
                          type="number"
                          style={{ width: 100 }}
                          size="large"
                          disabled={
                            !inputData?.gateEarliestAdvanceTime || disabled
                          }
                          placeholder="0"
                          value={inputData?.earliestAdvanceTimeNumber}
                          onChange={(e) =>
                            onUpdateInputData(
                              'earliestAdvanceTimeNumber',
                              e.target.value
                            )
                          }
                        />
                        <Select
                          style={{ width: 100 }}
                          size="large"
                          disabled={
                            !inputData?.gateEarliestAdvanceTime || disabled
                          }
                          defaultValue="1"
                          options={[
                            { value: 'days', label: 'Days' },
                            { value: 'hours', label: 'Hours' },
                          ]}
                          value={inputData?.earliestAdvanceTimeType}
                          onChange={(value) =>
                            onUpdateInputData('earliestAdvanceTimeType', value)
                          }
                        />
                      </Space>
                    </Gutter>
                  </Row>
                </Gap>
              </Card>
              <Card className="wk-wfg-form__shift-distribution-card">
                <Gap vertical size="sm">
                  <Row style={{ marginTop: 16 }}>
                    <Checkbox
                      onChange={(e) =>
                        onUpdateInputData(
                          'gateOpenShiftsVisibility',
                          e.target.checked
                        )
                      }
                      checked={inputData?.gateOpenShiftsVisibility}
                      disabled={disabled}
                      className={clsx({
                        'wk-wfg-form-checkbox-disabled': disabled,
                      })}
                    >
                      Add delay to schedule release
                    </Checkbox>
                  </Row>
                  <Row>
                    <Gutter direction="left" size="lg">
                      <Gutter direction="bottom">
                        <Text color={disabled ? 'midnightLightest' : undefined}>
                          How long after schedule release should staff see
                          shifts in the mobile app?
                        </Text>
                      </Gutter>
                      <Space>
                        <Input
                          type="number"
                          style={{ width: 100 }}
                          size="large"
                          disabled={
                            !inputData?.gateOpenShiftsVisibility || disabled
                          }
                          placeholder="0"
                          value={
                            inputData?.openShiftsVisibilityDelayHoursNumber
                          }
                          onChange={(e) =>
                            onUpdateInputData(
                              'openShiftsVisibilityDelayHoursNumber',
                              e.target.value
                            )
                          }
                        />
                        <Select
                          style={{ width: 100 }}
                          size="large"
                          disabled={
                            !inputData?.gateOpenShiftsVisibility || disabled
                          }
                          defaultValue="1"
                          options={[
                            { value: 'days', label: 'Days' },
                            { value: 'hours', label: 'Hours' },
                          ]}
                          value={inputData?.openShiftsVisibilityDelayHoursType}
                          onChange={(value) =>
                            onUpdateInputData(
                              'openShiftsVisibilityDelayHoursType',
                              value
                            )
                          }
                        />
                      </Space>
                    </Gutter>
                  </Row>
                </Gap>
              </Card>
            </Gap>
          </Gutter>
          <style jsx>
            {`
              .wk-wfg-form-checkbox-disabled span {
                color: ${Colors.midnightLightest};
              }
              :global(.wk-wfg-form__shift-distribution-card) {
                width: 100%;
              }
            `}
          </style>
        </>
      ),
    },
  ];

  return (
    <Form layout="vertical">
      <Form.Item label="Group Name" required>
        <Input
          size="large"
          value={inputData?.name}
          id="wk-workforce-group-name-input"
          disabled={disableName || disabled}
          className="group-name-input"
          onChange={(e) => onUpdateInputData('name', e.target.value)}
        />
      </Form.Item>
      <Tabs items={tabItems} />
    </Form>
  );
}
